/**
 * Pinout interno — o que acontece dentro de um componente.
 *
 * **Começa escolhendo o componente**, como no Sistema de hoje (o menu de lá se
 * chama literalmente "Selecionar Componente"). Não é etapa burocrática: pinout
 * interno só existe dentro de um componente, e os catálogos — componente
 * interno e circuito interno — são dele, não do projeto. Sem essa escolha não
 * há o que listar nem com que preencher.
 *
 * A grade e o lançamento seguem a mesma gramática do pinout externo: uma linha
 * por ligação, Enter anda, Ctrl+Enter grava, Espaço marca. O que muda é o
 * conteúdo — sem chicote, sem cor, sem conector, porque nada disso existe do
 * lado de dentro. No lugar entra a **ligação externa**: de qual pino do
 * componente esta linha sai.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { usarComponentes } from "@/api/catalogos"
import {
  usarCircuitosInternos, usarComponentesInternos, usarGravarInternos,
  usarPinoutsInternos, type FiltroInterno, type ItemInternoParaGravar, type PinoutInternoResponse,
} from "@/api/internos"
import { usarPinouts } from "@/api/pinouts"
import { usarModelos } from "@/api/catalogos"
import {
  baixarImagemInterna, gerarSvgInterno, nomeDoArquivo, type DesenhoPronto,
} from "@/api/grafico"
import { Baixar, DesenhoSvg, Trabalhando } from "@/kit/DesenhoSvg"
import { LoteInterno } from "./LoteInterno"
import { POR_PAGINA } from "@/api/query"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { LinhaDeAviso, Tabela, type Coluna } from "@/kit/Tabela"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { ApagarTodosOsPinouts } from "../ApagarTudo"
import { CampoDeBusca, idDe, textoDe, VAZIO, type ValorBusca } from "@/kit/CampoDeBusca"
import { TIPOS_PINOUT } from "../tipos"

export function PinoutInterno() {
  const { projetoId } = useParams()
  const projeto = Number(projetoId)

  const componentes = usarComponentes(projeto)
  const [componente, setComponente] = useState<ValorBusca>(VAZIO)
  const componenteId = idDe(componente)

  const [filtroPino, setFiltroPino] = useState("")
  const [aplicado, setAplicado] = useState<FiltroInterno>({})
  const [pagina, setPagina] = useState(1)
  const [marcados, setMarcados] = useState<Set<number>>(new Set())
  const [lancando, setLancando] = useState(false)
  const [desenhando, setDesenhando] = useState(false)

  // Trocar de componente zera tudo: nada do que estava na tela vale no novo.
  useEffect(() => {
    setPagina(1); setMarcados(new Set()); setLancando(false); setDesenhando(false)
    setFiltroPino(""); setAplicado({})
  }, [componenteId])

  const { data, isPending, error } = usarPinoutsInternos(componenteId, aplicado, pagina)

  const itens = data?.itens ?? []
  const total = data?.total ?? 0

  /** O nome do projeto: é o que a confirmação de "apagar todos" pede digitado. */
  const [recadoDeManutencao, setRecadoDeManutencao] = useState<string | null>(null)
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA))

  const colunas: Coluna<PinoutInternoResponse>[] = [
    {
      chave: "pino", rotulo: "Pino externo", largura: 110, mono: true, alinhamento: "direita",
      render: (p) => p.pino || <span style={{ color: "var(--txt3)" }}>—</span>,
    },
    {
      chave: "componenteInterno", rotulo: "Componente interno", minimo: 200,
      render: (p) => p.componenteInternoDescricao,
    },
    {
      chave: "circuitoInterno", rotulo: "Circuito interno", minimo: 160, mono: true,
      render: (p) => p.circuitoInternoDescricao,
    },
    { chave: "tipo", rotulo: "Tipo", largura: 130, render: (p) => TIPOS_PINOUT[p.tipo] ?? String(p.tipo) },
    { chave: "obs", rotulo: "Observação", minimo: 140, render: (p) => p.obs },
    { chave: "fonte", rotulo: "Fonte", largura: 140, render: (p) => p.arquivoOriginal },
  ]

  function marcar(ids: number[], marcar: boolean) {
    setMarcados((s) => {
      const n = new Set(s)
      for (const i of ids) marcar ? n.add(i) : n.delete(i)
      return n
    })
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        className="barra"
        style={{
          flex: "0 0 auto", display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: 10,
          padding: "10px 18px", background: "var(--surface2)", borderBottom: "1px solid var(--line2)",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 4, width: 300 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>Componente</span>
          <CampoDeBusca
            rotulo="Componente" valor={componente} aoMudar={setComponente}
            fonte={{ modo: "memoria", itens: componentes.itens, carregando: componentes.carregando }}
            permiteCriar={false} mono dica="código ou descrição" id="interno-componente"
          />
        </label>

        {componenteId > 0 && (
          <>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, width: 130 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>Pino</span>
              <input
                className="campo"
                value={filtroPino}
                onChange={(e) => setFiltroPino(e.target.value)}
                onKeyDown={(e) => {
                  // Enter aplica; nada de buscar a cada tecla (regra 2).
                  if (e.key === "Enter") { setAplicado({ pinos: filtroPino.trim() || undefined }); setPagina(1) }
                  if (e.key === "Escape") { setFiltroPino(""); setAplicado({}); setPagina(1) }
                }}
                placeholder="A01"
                aria-label="Filtrar por pino"
                style={{
                  width: "100%", padding: "0 9px", borderRadius: 7, border: "1px solid var(--line2)",
                  background: "var(--surface)", fontSize: 12.5, fontFamily: "var(--fm)",
                }}
              />
            </label>

            <span style={{ marginLeft: "auto" }} />

            {total > 0 && (
              <ApagarTodosOsPinouts
                projetoId={projeto}
                escopo="componente"
                alvoId={componenteId}
                nomeDoAlvo={textoDe(componente) || `componente ${componenteId}`}
                aoAvisar={setRecadoDeManutencao}
              />
            )}

            <button
              onClick={() => setLancando((l) => !l)}
              aria-expanded={lancando}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, height: 32, padding: "0 12px",
                borderRadius: 7, background: "var(--brand)", color: "#FFFFFF", fontSize: 12.5, fontWeight: 600,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"
                   style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round" }}>
                <path d="M12 5.5v13M5.5 12h13" />
              </svg>
              Lançar interno
            </button>

            <button
              onClick={() => setDesenhando((d) => !d)}
              aria-expanded={desenhando}
              style={{
                height: 32, padding: "0 12px", borderRadius: 7, border: "1px solid var(--line2)",
                background: "var(--surface)", fontSize: 12.5, fontWeight: 600, color: "var(--txt2)",
              }}
            >
              {desenhando ? "Fechar o desenho" : "Ver o desenho"}
            </button>
          </>
        )}
      </div>

      {lancando && componenteId > 0 && (
        <LancamentoInterno
          projetoId={projeto}
          componenteId={componenteId}
          componenteTexto={textoDe(componente)}
          aoFechar={() => setLancando(false)}
        />
      )}

      {desenhando && componenteId > 0 && (
        <DesenhoInterno
          componenteId={componenteId}
          componenteTexto={textoDe(componente)}
          projetoId={projeto}
        />
      )}

      {marcados.size > 0 && (
        <LoteInterno
          projetoId={projeto}
          componenteId={componenteId}
          marcados={marcados}
          linhas={itens}
          aoLimpar={() => setMarcados(new Set())}
        />
      )}

      <Tabela<PinoutInternoResponse>
        colunas={colunas}
        itens={itens}
        chaveDe={(p) => p.id}
        marcados={marcados}
        aoMarcar={marcar}
        vazio={
          componenteId <= 0 ? (
            <LinhaDeAviso>
              Escolha um componente acima.
              <span style={{ fontSize: 12, color: "var(--txt3)", maxWidth: "50ch" }}>
                O pinout interno existe dentro de um componente, e os catálogos de componente e
                circuito internos são dele — não do projeto.
              </span>
            </LinhaDeAviso>
          ) : isPending ? <LinhaDeAviso>Carregando…</LinhaDeAviso>
          : error ? (
            <LinhaDeAviso>
              <b style={{ color: "var(--erro)" }}>
                {error instanceof ErroDaApi ? error.message : "Não foi possível falar com o servidor."}
              </b>
            </LinhaDeAviso>
          ) : (
            <LinhaDeAviso>Este componente ainda não tem pinout interno.</LinhaDeAviso>
          )
        }
      />

      {recadoDeManutencao && (
        <p style={{
          margin: 0, padding: "7px 16px", fontSize: 11.5, lineHeight: 1.5,
          background: "var(--surface2)", color: "var(--txt2)",
          borderTop: "1px solid var(--line2)",
        }}>
          {recadoDeManutencao}
        </p>
      )}

      <BarraDeEstado
        direita={
          paginas > 1 ? (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Passo ligado={pagina > 1} rotulo="Página anterior" aoIr={() => setPagina(pagina - 1)} texto="‹" />
              <span className="num" style={{ minWidth: 62, textAlign: "center" }}>{pagina} de {paginas}</span>
              <Passo ligado={pagina < paginas} rotulo="Próxima página" aoIr={() => setPagina(pagina + 1)} texto="›" />
            </span>
          ) : null
        }
      >
        {total > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{total.toLocaleString("pt-BR")}</b>{" "}
            {total === 1 ? "interno" : "internos"}
          </span>
        )}
        {componenteId > 0 && (
          <span style={{ color: "var(--txt3)" }}>Espaço marca a linha</span>
        )}
      </BarraDeEstado>
    </div>
  )
}

/**
 * O lançamento interno. Mais curto que o externo porque a linha é mais curta:
 * ligação externa, componente interno, circuito interno, pino, tipo.
 *
 * A **ligação externa** é o campo que não existe do lado de fora: ela diz de
 * qual pino do componente esta linha interna sai. É opcional — há circuito
 * interno que não chega a pino nenhum.
 */
function LancamentoInterno({
  projetoId, componenteId, componenteTexto, aoFechar,
}: {
  projetoId: number
  componenteId: number
  componenteTexto: string
  aoFechar: () => void
}) {
  const compInternos = usarComponentesInternos(componenteId)
  const circInternos = usarCircuitosInternos(componenteId)
  const gravar = usarGravarInternos(projetoId, componenteId)

  // Os pinos do componente, para escolher a ligação externa.
  const externos = usarPinouts(projetoId, { componentes: String(componenteId) }, 1)

  const [ligacao, setLigacao] = useState<ValorBusca>(VAZIO)
  const [compInterno, setCompInterno] = useState<ValorBusca>(VAZIO)
  const [circInterno, setCircInterno] = useState<ValorBusca>(VAZIO)
  const [pino, setPino] = useState("")
  const [tipo, setTipo] = useState(0)
  const [obs, setObs] = useState("")
  const [fonte, setFonte] = useState("")
  const [erro, setErro] = useState<unknown>(null)
  const [gravou, setGravou] = useState(0)

  const pinosExternos = useMemo(
    () => (externos.data?.itens ?? []).map((p) => ({
      id: p.id,
      rotulo: [p.conectorDescricao, p.pino].filter(Boolean).join(" · ") || String(p.id),
      codigo: p.pino,
      usos: 0,
    })),
    [externos.data],
  )

  const faltando = compInterno.estado === "vazio" ? "Informe o componente interno."
    : circInterno.estado === "vazio" ? "Informe o circuito interno."
    : null

  async function enviar() {
    if (faltando) { setErro(new Error(faltando)); return }
    setErro(null)
    const item: ItemInternoParaGravar = {
      componenteId,
      pinoutInternoId: 0,
      pinoutId: idDe(ligacao),
      componenteInternoId: idDe(compInterno),
      componenteInternoCodigo: compInterno.estado === "novo" ? compInterno.texto : "",
      componenteInternoDescricao: compInterno.estado === "novo" ? compInterno.texto : "",
      circuitoInternoId: idDe(circInterno),
      circuitoInternoDescricao: circInterno.estado === "novo" ? circInterno.texto : "",
      restricaoId: 0,
      restricaoDescricao: "",
      pino: pino.trim(),
      obs,
      arquivoOriginal: fonte,
      tipo,
    }
    try {
      const r = await gravar.mutateAsync([item])
      setGravou(r.criados)
      // Limpa o que muda de linha para linha; mantém o que costuma repetir —
      // o mesmo critério do `LimparPinoutParcial` do lançamento externo.
      setCircInterno(VAZIO); setLigacao(VAZIO); setPino("")
    } catch (e) { setErro(e) }
  }

  return (
    <section
      onKeyDown={(ev) => {
        if (ev.key === "Enter" && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); void enviar() }
        if (ev.key === "Escape") { ev.preventDefault(); aoFechar() }
      }}
      style={{
        flex: "0 0 auto", padding: "10px 18px", background: "var(--surface)",
        borderBottom: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <b style={{ fontSize: 13 }}>Lançar pinout interno</b>
        <span className="mono" style={{ fontSize: 11.5, color: "var(--txt3)" }}>{componenteTexto}</span>
        <span style={{ marginLeft: "auto" }} />
        <button onClick={aoFechar} style={{ fontSize: 11.5, color: "var(--txt3)" }}>fechar</button>
      </div>

      <div style={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: 8 }}>
        <Campo rotulo="Ligação externa" largura={190}>
          <CampoDeBusca
            rotulo="Ligação externa" valor={ligacao} aoMudar={setLigacao}
            fonte={{ modo: "memoria", itens: pinosExternos, carregando: externos.isPending }}
            permiteCriar={false} mono dica="conector · pino" id="interno-ligacao"
          />
        </Campo>

        <Campo rotulo="Componente interno" largura={200} obrigatorio>
          <CampoDeBusca
            rotulo="Componente interno" valor={compInterno} aoMudar={setCompInterno}
            fonte={{ modo: "memoria", itens: compInternos.itens, carregando: compInternos.carregando }}
            dica="digite para achar ou criar" id="interno-componente-interno"
          />
        </Campo>

        <Campo rotulo="Circuito interno" largura={190} obrigatorio>
          <CampoDeBusca
            rotulo="Circuito interno" valor={circInterno} aoMudar={setCircInterno}
            fonte={{ modo: "memoria", itens: circInternos.itens, carregando: circInternos.carregando }}
            mono dica="digite para achar ou criar" id="interno-circuito-interno"
          />
        </Campo>

        <Campo rotulo="Pino" largura={100}>
          <input className="campo" value={pino} onChange={(e) => setPino(e.target.value)}
                 aria-label="Pino" placeholder="A01" style={{ ...entrada, fontFamily: "var(--fm)" }} />
        </Campo>

        <Campo rotulo="Tipo" largura={150}>
          <select className="campo" value={tipo} aria-label="Tipo"
                  onChange={(e) => setTipo(Number(e.target.value))} style={entrada}>
            {/* Record, não array: o pinout não tem os tipos 9 e 10 — são doze,
                não catorze, e a lista espelha a RN-10. */}
            {Object.entries(TIPOS_PINOUT).map(([v, t]) => (
              <option key={v} value={v}>{t}</option>
            ))}
          </select>
        </Campo>

        <Campo rotulo="Observação" largura={160}>
          <input className="campo" value={obs} onChange={(e) => setObs(e.target.value)}
                 aria-label="Observação" style={entrada} />
        </Campo>

        <Campo rotulo="Fonte" largura={140}>
          <input className="campo" value={fonte} onChange={(e) => setFonte(e.target.value)}
                 aria-label="Fonte" style={entrada} />
        </Campo>

        <button
          onClick={() => void enviar()}
          disabled={gravar.isPending}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 13px",
            borderRadius: 7, background: "var(--brand)", color: "#FFFFFF", fontSize: 12.5, fontWeight: 600,
            opacity: gravar.isPending ? 0.6 : 1,
          }}
        >
          {gravar.isPending ? "Gravando…" : "Gravar"}
          <kbd style={{ fontSize: 10, opacity: 0.8 }}>Ctrl ↵</kbd>
        </button>
      </div>

      {faltando && !erro && (
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>{faltando}</span>
      )}
      {gravou > 0 && !erro && (
        <span style={{ fontSize: 11.5, color: "var(--ok-on-soft)" }}>
          {gravou} {gravou === 1 ? "linha gravada" : "linhas gravadas"}.
        </span>
      )}
      {erro != null && (
        <div style={{
          padding: "9px 11px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
          background: erro instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
          color: erro instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
        }}>
          {erro instanceof ErroDeTrava || erro instanceof ErroDaApi || erro instanceof Error
            ? erro.message : "Não foi possível gravar."}
        </div>
      )}
    </section>
  )
}

const entrada: React.CSSProperties = {
  width: "100%", padding: "0 9px", borderRadius: 7,
  border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
}

function Campo({ rotulo, largura, obrigatorio, children }: {
  rotulo: string; largura: number; obrigatorio?: boolean; children: React.ReactNode
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, width: largura }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>
        {rotulo}{obrigatorio && <span style={{ color: "var(--erro)", marginLeft: 3 }}>*</span>}
      </span>
      {children}
    </label>
  )
}

function Passo({ ligado, rotulo, aoIr, texto }: {
  ligado: boolean; rotulo: string; aoIr: () => void; texto: string
}) {
  return (
    <button
      disabled={!ligado} onClick={aoIr} aria-label={rotulo}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: 5, border: "1px solid var(--line2)",
        background: "var(--surface)", color: "var(--txt2)", fontSize: 13,
        opacity: ligado ? 1 : 0.4, cursor: ligado ? "pointer" : "default",
      }}
    >
      {texto}
    </button>
  )
}


/**
 * O desenho do pinout interno.
 *
 * O motor tem os dois lados — `Montar_pinout` e `Montar_pinout_interno` — e a
 * API expõe os dois. Só a tela é que não usava o de dentro: construí esta grade
 * e esqueci do desenho, que já estava pronto do outro lado.
 *
 * Mais curto que o de fora: **não há opção de texto nenhuma**, porque não há
 * componente, conector nem circuito externos para nomear. O que decide o desenho
 * é o componente e o modelo — que é quem aplica as restrições aos pinos
 * internos. E, como lá, nada é reaproveitado: cada "Gerar" refaz.
 */
function DesenhoInterno({ componenteId, componenteTexto, projetoId }: {
  componenteId: number
  componenteTexto: string
  projetoId: number
}) {
  const modelos = usarModelos(projetoId)
  const [modeloId, setModeloId] = useState(0)
  const [desenho, setDesenho] = useState<DesenhoPronto | null>(null)
  const [gerando, setGerando] = useState(false)
  const [decorrido, setDecorrido] = useState(0)
  const [erro, setErro] = useState<unknown>(null)
  const [zoom, setZoom] = useState(1)
  const cancelador = useRef<AbortController | null>(null)

  useEffect(() => {
    if (modeloId === 0 && modelos.itens.length > 0) setModeloId(modelos.itens[0]!.id)
  }, [modelos.itens, modeloId])

  useEffect(() => {
    if (!gerando) return
    const t = setInterval(() => setDecorrido((x) => x + 0.1), 100)
    return () => clearInterval(t)
  }, [gerando])

  useEffect(() => () => cancelador.current?.abort(), [])

  const pedido = { componenteId, modeloId }
  const nome = nomeDoArquivo(projetoId, componenteTexto || String(componenteId), "interno")

  async function gerar() {
    if (modeloId <= 0 || gerando) return
    cancelador.current?.abort()
    const c = new AbortController()
    cancelador.current = c
    setGerando(true); setDecorrido(0); setErro(null)
    try {
      setDesenho(await gerarSvgInterno(pedido, c.signal))
      setZoom(1)
    } catch (e) {
      if (!c.signal.aborted) setErro(e)
    } finally {
      if (cancelador.current === c) { setGerando(false); cancelador.current = null }
    }
  }

  return (
    <section style={{
      flex: "0 0 auto", borderBottom: "1px solid var(--line)", background: "var(--surface3)",
      display: "flex", flexDirection: "column", maxHeight: "60vh",
    }}>
      <div className="barra" style={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
        padding: "9px 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
      }}>
        <b style={{ fontSize: 13 }}>Desenho interno</b>

        <select
          className="campo" value={modeloId} aria-label="Modelo"
          onChange={(e) => setModeloId(Number(e.target.value))}
          style={{
            width: 260, padding: "0 9px", borderRadius: 7, border: "1px solid var(--line2)",
            background: "var(--surface2)", fontSize: 12.5,
          }}
        >
          {modelos.itens.length === 0 && <option value={0}>Sem modelo neste projeto</option>}
          {modelos.itens.map((m) => <option key={m.id} value={m.id}>{m.rotulo}</option>)}
        </select>

        <button
          onClick={() => (gerando ? cancelador.current?.abort() : void gerar())}
          disabled={!gerando && modeloId <= 0}
          style={{
            height: 32, padding: "0 13px", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
            background: gerando ? "var(--surface)" : "var(--brand)",
            color: gerando ? "var(--txt2)" : "#FFFFFF",
            border: gerando ? "1px solid var(--line2)" : undefined,
            opacity: !gerando && modeloId <= 0 ? 0.45 : 1,
          }}
        >
          {gerando ? `Cancelar · ${decorrido.toFixed(1)} s` : desenho ? "Gerar de novo" : "Gerar desenho"}
        </button>

        {desenho && !gerando && (
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <span className="num" style={{ fontSize: 11.5, color: "var(--txt3)" }}>
              {desenho.segundos.toFixed(1)} s
            </span>
            <button onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}
                    aria-label="Diminuir o desenho" style={passo}>−</button>
            <span className="num" style={{ minWidth: 40, textAlign: "center", fontSize: 11.5 }}>
              {Math.round(zoom * 100)}%
            </span>
            <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
                    aria-label="Aumentar o desenho" style={passo}>+</button>
            <Baixar rotulo="PNG" aoBaixar={() => void baixarImagemInterna(pedido, "png", nome)} />
            <Baixar rotulo="EPS" aoBaixar={() => void baixarImagemInterna(pedido, "eps", nome)} />
          </span>
        )}
      </div>

      <div style={{
        flex: 1, minHeight: 0, overflow: "auto", padding: 16,
        display: "flex", justifyContent: "center",
        alignItems: gerando || !desenho ? "center" : "flex-start",
      }}>
        {gerando ? (
          <Trabalhando segundos={decorrido} />
        ) : erro ? (
          <span style={{ fontSize: 12.5, color: "var(--erro)", textAlign: "center", maxWidth: "48ch" }}>
            {erro instanceof ErroDaApi || erro instanceof Error
              ? erro.message : "Não foi possível gerar o desenho."}
          </span>
        ) : desenho ? (
          <DesenhoSvg
            svg={desenho.svg} largura={desenho.largura} zoom={zoom}
            rotulo={`Pinout interno — ${componenteTexto}`}
          />
        ) : (
          <span style={{ fontSize: 12.5, color: "var(--txt3)", textAlign: "center", maxWidth: "48ch", lineHeight: 1.6 }}>
            Escolha o modelo e aperte <b>Gerar desenho</b>. Como o de fora, ele é sempre refeito na
            hora — mostra o pinout interno como está agora, não como estava.
          </span>
        )}
      </div>
    </section>
  )
}

const passo: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center",
  width: 22, height: 22, borderRadius: 5, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--txt2)", fontSize: 13, fontWeight: 700,
}
